import { useMemo } from 'react';

import { LegalClientBodyEntity } from 'api/types/entity';
import { GridFields } from 'components';
import { getYesOrNoOptions } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { Stack } from 'libs/ui';
import { Section } from 'modules/client/common/components';
import { DocumentsTableFeature } from 'modules/client/common/feature';
import {
  Field,
  useFormContext,
} from 'modules/client/common/forms/legalClientHeadPositionForm';

interface Props {
  bodies?: LegalClientBodyEntity[];
  onlyPosition?: boolean;
}

export const LegalClientAddHeadPositionSection = ({
  bodies = [],
  onlyPosition,
}: Props) => {
  const { t } = useTranslation();
  const { values, readOnly } = useFormContext();

  const optionsBodies = useMemo(() => {
    const optionsBodies = bodies.map((b) => ({
      value: b.id,
      label: b.name,
    }));

    optionsBodies.push({
      label: t('client.legal.legalClientHead.position.body.notBelongOrgans'),
      value: '-1',
    });
    return optionsBodies;
  }, [bodies, t]);

  return (
    <Section
      datax="legalClientAddHeadPositionSection"
      title={t('client.legal.legalClientHead.position.title')}
    >
      <Stack spacing={24}>
        <GridFields>
          {!onlyPosition && (
            <>
              <Field.Autocomplete
                multiple
                showRequiredMark
                getOptionDisabled={(data) => {
                  const option = data as { value: string };
                  if (values.position.bodies?.find((v) => v?.value === '-1')) {
                    return option.value !== '-1';
                  }
                  if (values.position.bodies?.length) {
                    return option.value === '-1';
                  }

                  return false;
                }}
                label={t('client.legal.legalClientHead.position.body.label')}
                name="position.bodies"
                optionLabelKey="label"
                options={optionsBodies}
                optionValueKey="value"
                placeholder={t(
                  'client.legal.legalClientHead.position.body.placeholder'
                )}
                variant="select"
              />

              <Field.Autocomplete
                fullWidth
                showRequiredMark
                label={t(
                  'client.legal.legalClientHead.position.firstHead.label'
                )}
                name="position.firstHead"
                optionLabelKey="label"
                options={getYesOrNoOptions(t)}
                optionValueKey="value"
                placeholder={t(
                  'client.legal.legalClientHead.position.firstHead.placeholder'
                )}
                variant="select"
              />
            </>
          )}

          <Field.Text
            fullWidth
            showRequiredMark
            label={t('client.legal.legalClientHead.position.position.label')}
            name="position.position"
            placeholder={t(
              'client.legal.legalClientHead.position.position.placeholder'
            )}
          />
          <Field.DatePicker
            fullWidth
            disableFuture={false}
            label={t(
              'client.legal.legalClientHead.position.dateOfExpiration.label'
            )}
            name="position.dateOfExpiration"
            placeholder={t(
              'client.legal.legalClientHead.position.dateOfExpiration.placeholder'
            )}
          />
        </GridFields>

        <DocumentsTableFeature
          name="position.positionConfirmationFiles"
          readOnly={readOnly}
        />
      </Stack>
    </Section>
  );
};
